import { AssetType, IPersonAsset, OriginAssetType } from './IPersonAsset';
import { IVehicle, VehicleKind } from './IVehicle';

export enum PolicyType {
  Home = 'home',
  HomeWarranty = 'home_warranty',
  Condo = 'condo',
  Fire = 'fire',
  Renters = 'renters',
  ManufacturedOrMobileHome = 'manufactured_or_mobile_home',

  Auto = 'auto',
  Moto = 'moto',
  RV = 'rv',
  Boat = 'boat',

  Flood = 'flood',
  Earthquake = 'earthquake',
  WindOnly = 'wind_only',
  Umbrella = 'umbrella',

  PAF = 'paf',
  TermLife = 'term_life',
  WholeLife = 'whole_life',
  ADND = 'accidental_death_and_dismemberment',
  AD = 'accidental_death',
  Pet = 'pet'
}

export enum InsuranceCategory {
  PersonalLines = 'personal_lines',
  Life = 'life'
}

export enum Requirement {
  Essential = 'essential',
  NonEssential = 'non_essential'
}

export enum InsurableInterest {
  RealProperty = 'real_property',
  Vehicle = 'vehicle',
  PersonalBelongings = 'personal_belongings',
  Life = 'life'
}

const PERIOD_YEAR = 12;

const type = ({
  key,
  name,
  short_name,
  requirement,
  insurable_interest,
  with_primary_coverage,
  possible_origination,
  possible_terms,
  is_ancillary,
  category
}: {
  key: PolicyType;
  name: string;
  short_name: string;
  requirement: Requirement;
  insurable_interest: InsurableInterest | null;
  with_primary_coverage: boolean;
  possible_origination: boolean;
  possible_terms: number[];
  is_ancillary: boolean;
  category: InsuranceCategory;
}) =>
  ({
    key,
    name,
    short_name,
    requirement,
    insurable_interest,
    with_primary_coverage,
    possible_origination,
    possible_terms,
    is_ancillary,
    with_variable_period: possible_terms.length > 1,
    category
  }) as const;

export const PolicyTypes = [
  type({
    key: PolicyType.Home,
    name: 'Home',
    short_name: 'HO3',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: true,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Condo,
    name: 'Condo',
    short_name: 'HO6',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: true,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Fire,
    name: 'Dwelling/Fire',
    short_name: 'DP3',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: true,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Renters,
    name: 'Renters',
    short_name: 'HO4',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.ManufacturedOrMobileHome,
    name: 'Mobile / manufactured home',
    short_name: 'HO7',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: true,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Auto,
    name: 'Auto',
    short_name: '',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.Vehicle,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR / 2, PERIOD_YEAR],
    is_ancillary: false,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Moto,
    name: 'Motorcycle',
    short_name: '',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.Vehicle,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR / 2, PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.RV,
    name: 'RV',
    short_name: '',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.Vehicle,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR / 2, PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Boat,
    name: 'Boat',
    short_name: '',
    requirement: Requirement.Essential,
    insurable_interest: InsurableInterest.Vehicle,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),

  type({
    key: PolicyType.Flood,
    name: 'Flood',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: true,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Earthquake,
    name: 'Earthquake',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.WindOnly,
    name: 'Wind only',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Umbrella,
    name: 'Umbrella',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.HomeWarranty,
    name: 'Home Warranty',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.RealProperty,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.PAF,
    name: 'PAF',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.PersonalBelongings,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.Pet,
    name: 'Pet',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.Life,
    with_primary_coverage: false,
    possible_origination: false,
    possible_terms: [PERIOD_YEAR],
    is_ancillary: true,
    category: InsuranceCategory.PersonalLines
  }),
  type({
    key: PolicyType.TermLife,
    name: 'Term life',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.Life,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [
      5 * PERIOD_YEAR,
      10 * PERIOD_YEAR,
      15 * PERIOD_YEAR,
      20 * PERIOD_YEAR,
      25 * PERIOD_YEAR,
      30 * PERIOD_YEAR
    ],
    is_ancillary: true,
    category: InsuranceCategory.Life
  }),
  type({
    key: PolicyType.ADND,
    name: 'AD&D',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.Life,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [
      5 * PERIOD_YEAR,
      10 * PERIOD_YEAR,
      15 * PERIOD_YEAR,
      20 * PERIOD_YEAR,
      25 * PERIOD_YEAR,
      30 * PERIOD_YEAR
    ],
    is_ancillary: true,
    category: InsuranceCategory.Life
  }),
  type({
    key: PolicyType.AD,
    name: 'Accidental Death',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.Life,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [
      5 * PERIOD_YEAR,
      10 * PERIOD_YEAR,
      15 * PERIOD_YEAR,
      20 * PERIOD_YEAR,
      25 * PERIOD_YEAR,
      30 * PERIOD_YEAR
    ],
    is_ancillary: true,
    category: InsuranceCategory.Life
  }),
  type({
    key: PolicyType.WholeLife,
    name: 'Whole life',
    short_name: '',
    requirement: Requirement.NonEssential,
    insurable_interest: InsurableInterest.Life,
    with_primary_coverage: true,
    possible_origination: false,
    possible_terms: [
      5 * PERIOD_YEAR,
      10 * PERIOD_YEAR,
      15 * PERIOD_YEAR,
      20 * PERIOD_YEAR,
      25 * PERIOD_YEAR,
      30 * PERIOD_YEAR
    ],
    is_ancillary: true,
    category: InsuranceCategory.Life
  })
] as const;

export const findPolicyType = (policyType: PolicyType | string) => PolicyTypes.find(({ key }) => key === policyType);

export const isRequirementEssential = (policyType: PolicyType) =>
  findPolicyType(policyType)?.requirement === Requirement.Essential;

export const isRequirementNonEssential = (policyType: PolicyType) =>
  findPolicyType(policyType)?.requirement === Requirement.NonEssential;

export const isInsurableInterestRealProperty = (policyType: PolicyType) =>
  findPolicyType(policyType)?.insurable_interest === InsurableInterest.RealProperty;

export const isHomePolicyType = (policyType: PolicyType) =>
  isRequirementEssential(policyType) && isInsurableInterestRealProperty(policyType);

export const isInsurableInterestVehicle = (policyType: PolicyType) =>
  findPolicyType(policyType)?.insurable_interest === InsurableInterest.Vehicle;

export const isInsurableInterestLife = (policyType: PolicyType) =>
  findPolicyType(policyType)?.insurable_interest === InsurableInterest.Life;

export const isAncillary = (policyType: PolicyType) => findPolicyType(policyType)?.is_ancillary;

const assetTypeForPolicyType = (policyType: PolicyType) => {
  if (isInsurableInterestRealProperty(policyType)) {
    return AssetType.Home;
  }

  if (isInsurableInterestVehicle(policyType)) {
    return AssetType.Auto;
  }
};

export const originAssetTypeForPolicyType = (policyType: PolicyType) => {
  if (isInsurableInterestRealProperty(policyType)) {
    return OriginAssetType.Home;
  }

  if (isInsurableInterestVehicle(policyType)) {
    return OriginAssetType.Vehicle;
  }
};

export const assetsForPolicyType = (policyType: PolicyType, allAssets: IPersonAsset[]) => {
  if (isInsurableInterestRealProperty(policyType)) {
    return allAssets.filter(asset => asset?.asset_name === AssetType.Home);
  }

  if (isInsurableInterestVehicle(policyType)) {
    return allAssets.filter(asset => asset?.asset_name === AssetType.Auto);
  }

  return [];
};

export const doesPolicyTypeRequireAsset = (policyType: PolicyType) => !!assetTypeForPolicyType(policyType);

export const isOnlyOneAssetSupported = (policyType: PolicyType) => isInsurableInterestRealProperty(policyType);

export const areManyAssetsSupported = (policyType: PolicyType) => isInsurableInterestVehicle(policyType);

export const doesPolicyTypeHavePrimaryCoverage = (policyType: PolicyType) =>
  findPolicyType(policyType)?.with_primary_coverage;

export const possiblePolicyTerms = (policyType: PolicyType) =>
  findPolicyType(policyType)?.possible_terms || [PERIOD_YEAR];

export const findPolicyTypeLabel = (policyType: PolicyType | null | undefined) =>
  policyType ? findPolicyType(policyType)?.name || '' : '';

export const doesPolicyTypeSupportAgentOfRecord = (policyType: PolicyType) => policyType !== PolicyType.TermLife;

export const doesPolicyTypeSupportNewClosing = (policyType: PolicyType): boolean =>
  isInsurableInterestRealProperty(policyType) && isRequirementEssential(policyType);

export const arePolicyTypesSimilar = (left: PolicyType, right: PolicyType) =>
  left === right ||
  (isRequirementEssential(left) &&
    isInsurableInterestRealProperty(left) &&
    isRequirementEssential(right) &&
    isInsurableInterestRealProperty(right)) ||
  (isInsurableInterestVehicle(left) && isInsurableInterestVehicle(right));

export const isNonLifeAncillaryPolicyType = ({ policy_type }: { policy_type: PolicyType }) => {
  const policyType = findPolicyType(policy_type);
  if (!policyType) {
    return false;
  }

  return policyType.is_ancillary && policyType.category !== InsuranceCategory.Life;
};

export const policyTypesSimilarTo = ({ policy_type }: { policy_type: PolicyType }) =>
  PolicyTypes.filter(({ key }) => arePolicyTypesSimilar(key, policy_type));

export const policyTypesForCategory = ({ insuranceCategory }: { insuranceCategory: InsuranceCategory }) =>
  PolicyTypes.filter(({ category }) => insuranceCategory === category).map(({ key }) => key);

export const policyTypeForVehicleAsset = (vehicle: IVehicle) => {
  if (vehicle.kind === VehicleKind.MOTORCYCLE) {
    return PolicyType.Moto;
  }

  if (vehicle.kind === VehicleKind.TRAILER) {
    return PolicyType.RV;
  }

  if (vehicle.kind === VehicleKind.BOAT) {
    return PolicyType.Boat;
  }

  return PolicyType.Auto;
};

export const PL_DEFAULT_OPTIONS = [
  {
    value: PolicyType.Home,
    label: findPolicyTypeLabel(PolicyType.Home)
  },
  {
    value: PolicyType.Condo,
    label: findPolicyTypeLabel(PolicyType.Condo)
  },
  {
    value: PolicyType.Fire,
    label: findPolicyTypeLabel(PolicyType.Fire)
  },
  {
    value: PolicyType.Auto,
    label: findPolicyTypeLabel(PolicyType.Auto)
  },
  {
    value: PolicyType.Flood,
    label: findPolicyTypeLabel(PolicyType.Flood)
  },
  {
    value: PolicyType.Earthquake,
    label: findPolicyTypeLabel(PolicyType.Earthquake)
  },
  {
    value: PolicyType.Umbrella,
    label: findPolicyTypeLabel(PolicyType.Umbrella)
  },
  {
    value: PolicyType.PAF,
    label: findPolicyTypeLabel(PolicyType.PAF)
  }
];

export const LIFE_DEFAULT_OPTIONS = [
  {
    value: PolicyType.TermLife,
    label: findPolicyTypeLabel(PolicyType.TermLife)
  },
  {
    value: PolicyType.ADND,
    label: findPolicyTypeLabel(PolicyType.ADND)
  }
];

export const policyTypeOrderMap = new Map(PolicyTypes.map((typeObj, index) => [typeObj.key, index]));
